import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import Bugsnag from '@bugsnag/browser';
import {
  RootState,
  AppDispatch,
  bearerAuth,
  cartAuth,
  changeLocation,
  contractViolationErrors,
  getClient,
  validationErrors,
} from '@src/core/store';
import {
  Cart,
  CartItem,
  AddCartItemRequest,
  ErrorResponse,
  AddProductPackageToCartRequest,
} from '@src/core/buyerApiClient';
import { AxiosError, AxiosResponse } from 'axios';
import { loadSaleOrigin, loadAffiliationCode } from '@src/common';
import * as storage from '@src/common/storage';
import { setProductAdderStatus } from '@src/stores/product';
import { publicApiTokenSelector } from '@src/stores/app';
import { isCouponErrorType, toCouponErrorKeys, type CouponErrorsType } from '@src/features/coupons/core/couponErrors';
import { isCartReady, hasCartErrors, hasCheckoutableItems } from '@src/domains/cart';

const planneWidgetCartKey = 'planneWidgetCart';

export interface CartState {
  cart?: Cart;
  cartItemAdded?: boolean;
  cartItemAddedWidgetId?: string;
  cartItemAddedErrors?: { [key: string]: string };
  couponErrors?: CouponErrorsType[];
  isModalOpened: boolean;
  isLoadingCart: boolean;
  isLoadingCoupon: boolean;
}

const initialState: CartState = {
  isModalOpened: false,
  isLoadingCart: true,
  isLoadingCoupon: false,
};

interface CartAddResponse {
  ok: boolean;
  errors?: { [key: string]: string[] };
}

export const cartSlice = createSlice({
  name: 'cart',
  initialState,
  reducers: {
    setCart: (state: CartState, action: PayloadAction<Cart, string>) => {
      state.cart = action.payload;
    },
    requestCartItemAdded: (state: CartState, action: PayloadAction<string | undefined>) => {
      state.cartItemAdded = undefined;
      state.cartItemAddedWidgetId = action.payload;
      state.cartItemAddedErrors = undefined;
    },
    receiveCartItemAdded: (state: CartState, { payload: { ok, errors } }: PayloadAction<CartAddResponse, string>) => {
      state.cartItemAdded = ok;

      if (errors) {
        state.cartItemAddedErrors = Object.keys(errors).reduce(
          (carry, key) => ({ ...carry, [key]: errors[key]!.join(', ') }),
          {},
        );
      }
    },
    setCartItemAddedErrors: (state: CartState) => {
      state.cartItemAddedErrors = undefined;
    },
    clearCartItemAddedErrors: (state: CartState) => {
      state.cartItemAddedErrors = undefined;
    },
    setCouponErrors: (state: CartState, action: PayloadAction<CouponErrorsType[] | undefined, string>) => {
      state.couponErrors = action.payload;
    },
    setCartModalOpenState: (state: CartState, action: PayloadAction<boolean, string>) => {
      state.isModalOpened = action.payload;
    },
    setLoadingCart: (state: CartState, action: PayloadAction<boolean>) => {
      state.isLoadingCart = action.payload;
    },
    setLoadingCoupon: (state: CartState, action: PayloadAction<boolean>) => {
      state.isLoadingCoupon = action.payload;
    },
  },
});

export default cartSlice.reducer;

export const {
  setCart,
  requestCartItemAdded,
  receiveCartItemAdded,
  setCouponErrors,
  clearCartItemAddedErrors,
  setCartModalOpenState,
} = cartSlice.actions;
const { setLoadingCart, setLoadingCoupon } = cartSlice.actions;

// `setupCart` runs more than once per page: `initializeStore` loads the stored cart and a cart direct
// link creates its own. Only the latest call may register its cart, otherwise a slower stale response
// (e.g. the stored cart failing and falling back to a new empty one) replaces the direct link cart.
let latestSetupCartRequest = 0;

const isStaleSetupCartRequest = (requestId?: number) => requestId !== undefined && requestId !== latestSetupCartRequest;

export const setupCart =
  (directLinkCode?: string, forceNew?: boolean) => async (dispatch: AppDispatch, getState: () => RootState) => {
    dispatch(setLoadingCart(true));

    const publicApiKey = publicApiTokenSelector(getState());
    const { cartId, shouldHighlight } = retrieveCartId(publicApiKey)!;
    const originCode = loadSaleOrigin();
    const [affiliationCode, changed] = loadAffiliationCode();

    const requestId = ++latestSetupCartRequest;

    const cartRequest =
      !directLinkCode && cartId != null && !forceNew
        ? dispatch(loadCart(cartId!, originCode || undefined, requestId))
        : dispatch(obtainNewCart(originCode || undefined, directLinkCode, affiliationCode, requestId));

    if (cartId && affiliationCode && changed) {
      dispatch(setCartAffiliation(cartId, affiliationCode));
    }

    if (shouldHighlight) {
      dispatch(openCartModal());
    }

    await cartRequest;
  };

const cartIdQueryParam = 'planneCartId';

const retrieveCartId = (publicApiKey: string): { cartId?: string; shouldHighlight: boolean } => {
  const params = new URLSearchParams(window.location.search);
  const publicCartId = params.get(cartIdQueryParam);

  if (publicCartId) {
    storage.setValue(publicApiKey, planneWidgetCartKey, publicCartId);

    // Remove cartId from URL
    params.delete(cartIdQueryParam);
    window.history.replaceState(
      {},
      document.title,
      window.location.pathname + (params.size ? '?' + params.toString() : ''),
    );
  }

  return {
    cartId: storage.getValue(publicApiKey, planneWidgetCartKey),
    shouldHighlight: !!publicCartId,
  };
};

export const loadCart =
  (cartId?: string, origin?: string, requestId?: number) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    const state = getState();
    if (cartId == null) {
      const {
        cart: { cart },
      } = state;

      if (cart) {
        cartId = cart.id;
      } else {
        return;
      }
    }

    let response: AxiosResponse<Cart>;
    try {
      response = await getClient(state).carts.getCart(cartId, cartAuth(state));
    } catch (err) {
      await dispatch(obtainNewCart(origin, undefined, undefined, requestId));
      return;
    }

    if (isStaleSetupCartRequest(requestId)) {
      return;
    }

    const { data: cart } = response!;

    dispatch(registerCart(cart));
    dispatch(ensureSaleOrigin(cart, origin));
  };

export const ensureSaleOrigin =
  (cart: Cart, saleOriginCode?: string) => async (_: AppDispatch, getState: () => RootState) => {
    if (saleOriginCode == null || cart.saleOrigin?.code === saleOriginCode) {
      return;
    }

    const state = getState();

    try {
      await getClient(state).carts.setCartSaleOrigin(cart.id, { saleOriginCode }, cartAuth(state));
    } catch (err) {
      console.error('Could not associate sale origin!');

      throw err;
    }
  };

export const obtainNewCart =
  (saleOriginCode?: string, directLinkCode?: string, affiliationCode?: string | null, requestId?: number) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    let response: AxiosResponse<Cart>;
    affiliationCode = affiliationCode ?? undefined;
    try {
      response = await getClient(getState()).carts.createCart({ directLinkCode, saleOriginCode, affiliationCode });
    } catch (err) {
      if (!isStaleSetupCartRequest(requestId)) {
        dispatch(setLoadingCart(false));
        dispatch(setLoadingCoupon(false));
      }

      console.error('Could not create cart!');

      throw err;
    }

    if (isStaleSetupCartRequest(requestId)) {
      return;
    }

    const { data: cart } = response;
    dispatch(registerCart(cart));
  };

export const registerCart = (cart: Cart) => async (dispatch: AppDispatch, getState: () => RootState) => {
  const publicApiKey = publicApiTokenSelector(getState());

  const { cart: cartWithoutCouponErrors, couponErrors } = extractCouponErrorsFromCartGeneralErrors(cart);

  dispatch(registerCouponErrors(couponErrors));

  dispatch(setCart(cartWithoutCouponErrors));
  storage.setValue(publicApiKey!, planneWidgetCartKey, cart.id);

  dispatch(setLoadingCart(false));
  dispatch(setLoadingCoupon(false));
};

const CART_ID_QUERY_PARAM = 'planneCartId';

const withCartId = (landingPage: string, cartId: string) => {
  const separator = landingPage.includes('?') ? '&' : '?';

  return `${landingPage}${separator}${CART_ID_QUERY_PARAM}=${cartId}`;
};

export const restoreCartFromOrder =
  (orderId: string, landingPage: string) =>
  async (dispatch: AppDispatch, getState: () => RootState): Promise<boolean> => {
    const state = getState();
    const buyerToken = state.auth.buyerToken;

    if (!buyerToken) {
      return false;
    }

    let cart;

    try {
      const response = await getClient(state).carts.convertOrderToCart(orderId, bearerAuth(buyerToken));

      cart = response.data;
    } catch (error) {
      Bugsnag.notify(
        { name: 'RestoreCartFromOrderError', message: 'Could not rebuild the cart from the order' },
        (event) => event.addMetadata('error', { error, orderId }),
      );
    }

    if (!cart) {
      return false;
    }

    await dispatch(registerCart(cart));
    dispatch(changeLocation(withCartId(landingPage, cart.id)));

    return true;
  };

export const clearCartReference = () => async (dispatch: AppDispatch, getState: () => RootState) => {
  const publicApiKey = publicApiTokenSelector(getState());
  storage.removeValue(publicApiKey, planneWidgetCartKey);
};

export const setCartAffiliation =
  (cartId: string, affiliationCode: string) => async (dispatch: AppDispatch, getState: () => RootState) => {
    const state = getState();
    const { data } = await getClient(state).carts.setCartAffiliation(cartId, { affiliationCode }, cartAuth(state));

    dispatch(setCart(data));
  };

export const addToCart =
  (params: AddCartItemRequest | AddProductPackageToCartRequest, widgetId: string) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    const state = getState();
    const {
      cart: { cart },
    } = state;
    if (cart == null) {
      return;
    }
    dispatch(setProductAdderStatus({ id: widgetId, value: 'adding_to_cart' }));
    dispatch(requestCartItemAdded(widgetId));

    try {
      if ('items' in params && params.items.length > 0) {
        await addProductPackageToCart(state, cart.id, params as AddProductPackageToCartRequest);
      } else {
        await addCartItem(state, cart.id, params as AddCartItemRequest);
      }
    } catch (err) {
      // 422 carries the business rules — the product's per-transaction limit, the cart item limit.
      // 400 is the contract's own ceiling, refused at the gateway before the core sees it.
      const errors = validationErrors(err) ?? contractViolationErrors(err);
      if (errors) {
        dispatch(receiveCartItemAdded({ ok: false, errors }));
        return;
      }

      throw err;
    } finally {
      dispatch(setProductAdderStatus({ id: widgetId, value: 'finished_request' }));
    }

    dispatch(receiveCartItemAdded({ ok: true }));
  };

const addCartItem = async (state: RootState, cartId: string, params: AddCartItemRequest) => {
  const [affiliationCode] = loadAffiliationCode();

  if (affiliationCode) {
    params.affiliationCode = affiliationCode;
  }

  await getClient(state).carts.addCartItem(cartId, params, cartAuth(state));
};

const addProductPackageToCart = async (state: RootState, cartId: string, params: AddProductPackageToCartRequest) => {
  const [affiliationCode] = loadAffiliationCode();

  if (affiliationCode) {
    params.items = params.items.map((item) => ({
      ...item,
      affiliationCode,
    }));
  }

  await getClient(state).carts.addProductPackageToCart(cartId, params, cartAuth(state));
};

export const removeCartItem = (item: CartItem) => async (dispatch: AppDispatch, getState: () => RootState) => {
  const state = getState();
  const {
    cart: { cart },
  } = state;
  if (cart == null) {
    return;
  }

  await getClient(state).carts.removeCartItem(cart.id, item.id, cartAuth(state));
  dispatch(refreshCart());
};

export const removeCartPackage =
  (productPackageUnitId: string) => async (dispatch: AppDispatch, getState: () => RootState) => {
    const state = getState();
    const {
      cart: { cart },
    } = state;
    if (cart == null) {
      return;
    }

    await getClient(state).carts.removeProductPackageFromCart(cart.id, productPackageUnitId, cartAuth(state));
    dispatch(refreshCart());
  };

export const refreshCart = () => async (dispatch: AppDispatch, getState: () => RootState) => {
  const {
    cart: { cart },
  } = getState();

  if (cart) {
    dispatch(setLoadingCart(true));
    dispatch(loadCart());
  }
};

export const setCartCoupon = (code: string) => async (dispatch: AppDispatch, getState: () => RootState) => {
  const state = getState();
  const {
    cart: { cart },
  } = state;
  if (cart == null) {
    return;
  }
  dispatch(setLoadingCoupon(true));
  dispatch(setCouponErrors(undefined));

  let response: AxiosResponse<Cart>;
  try {
    response = await getClient(state).carts.setCartCoupon(cart.id, { code }, cartAuth(state));
  } catch (err) {
    const isValidationError = (err as AxiosError).response?.status === 422;

    const errors = isValidationError
      ? (err as AxiosError<ErrorResponse>).response!.data?.errors?.map((error) => error)
      : undefined;

    if (errors) {
      const errorKeys = toCouponErrorKeys(errors.map((e) => e.type));

      dispatch(setCouponErrors(errorKeys));
      dispatch(setLoadingCart(false));
      return;
    }

    throw err;
  } finally {
    dispatch(setLoadingCoupon(false));
  }

  const { data: newCart } = response!;
  dispatch(registerCart(newCart));
};

export const removeCartCoupon = () => async (dispatch: AppDispatch, getState: () => RootState) => {
  const state = getState();
  const {
    cart: { cart },
  } = state;
  if (cart == null) {
    return;
  }

  const { data: newCart } = await getClient(state).carts.removeCartCoupon(cart.id, cartAuth(state));
  dispatch(registerCart(newCart));
};

export const openCartModal = () => async (dispatch: AppDispatch) => {
  dispatch(setCartModalOpenState(true));
  dispatch(refreshCart());
};

export const closeCartModal = () => async (dispatch: AppDispatch) => {
  dispatch(setCartModalOpenState(false));
};

export const cartStateSelector = (state: RootState) => state.cart;
export const addCartItemAddedSelector = (state: RootState) => ({
  status: state.cart.cartItemAdded,
  errors: state.cart.cartItemAddedErrors,
  widgetId: state.cart.cartItemAddedWidgetId,
});
export const completeCartSelector = (state: RootState) => {
  const { cart, isLoadingCart, couponErrors } = state.cart;
  const cartItems = cart?.items ?? [];

  return {
    cart,
    isLoading: isLoadingCart,
    isReady: isCartReady({ cart, couponErrors }),
    hasCheckoutableItems: hasCheckoutableItems({ cart, couponErrors }),
    hasErrors: hasCartErrors(cart),
    cartErrors: cart?.generalErrors,
    cartItemErrors: cartItems.map((cartItem) => cartItem?.errors),
  };
};
export const cartSetCouponErrorsSelector = (state: RootState) => state.cart.couponErrors;

export const cartHasPartnershipWithCouponSelector = (state: RootState) => {
  const { cart } = state.cart;

  return cart?.coupon?.partnershipId != null;
};

export const cartCouponSelector = (state: RootState) => state.cart.cart?.coupon;
export const cartGeneralErrorsSelector = (state: RootState) => state.cart.cart?.generalErrors;

export const extractCouponErrorsFromCartGeneralErrors = (cart: Cart) => {
  const couponErrors = cart.generalErrors?.filter((error) => isCouponErrorType(error.type));

  if (couponErrors && couponErrors.length) {
    return {
      cart: {
        ...cart,
        generalErrors: cart.generalErrors?.filter((error) => !isCouponErrorType(error.type)),
      },
      couponErrors: couponErrors.map((error) => error.type),
    };
  }

  return { cart, couponErrors: [] as CouponErrorsType[] };
};

export const registerCouponErrors = (errors: string[]) => (dispatch: AppDispatch) => {
  const errorKeys = toCouponErrorKeys(errors);

  dispatch(setCouponErrors(errorKeys));
};
